import React, { useCallback, useMemo, useRef, useState } from "react";
import { Popup } from "devextreme-react/popup";
import { NumberBox, SelectBox } from "devextreme-react";
import DynamicFromLookup from "../../../../../dynamicFormBuilder/DynamicFromLookup";
import { ACCurrencyLayout } from "../../../../../common/lookups/Layouts/ACLayouts";
import { CommissionMessages, CommissionRateTimings, CurrencyTypes, DynamicLookupOptions } from "../../../../../enum/Enum";
import { OperationDialogFooter, OperationFieldset } from "./OperationDialogParts";
import "../../../../../dynamicFormBuilder/formField.css";

// The SPOPENITEMS columns uOMD0012 binds to dsSPOpenItems (OMD0000 ShowCommissionsCurrencyForm).
export const CURRENCY_FIELDS = ["TransCurrencyType", "RTransCurrencyCode", "PayCurrencyType", "RPayCurrencyCode", "RateTiming", "CurrencyRate"];

// uOMD0012 edtCurrencyRate DisplayFormat '$,0.000000'.
const CURRENCY_RATE_FORMAT = "#,##0.000000";

const currencyTypeOptions = [
    { id: CurrencyTypes.Home, text: "Home" },
    { id: CurrencyTypes.Foreign, text: "Foreign" },
];

// SPOPENITEMS.RATETIMING as the legacy SP data module lists it (uSPM0006 qrySPOpenItemsRATETIMING).
const rateTimingOptions = [
    { id: CommissionRateTimings.Preset, text: "Preset" },
    { id: CommissionRateTimings.AtCommit, text: "At Time Of Commit" },
];

// Legacy copied the OM header's RATETIMING onto new lines, where 'I' also means "at commit" (uOMM0000).
// Offered only on a line that already holds it, so opening the popup does not show it blank.
const orderRateTimingOption = { id: CommissionRateTimings.OMHeaderAtCommit, text: "At Time Of Commit (order)" };

const fieldRowStyle = { display: "flex", gap: 10 };
const fieldStyle = { flex: 1, minWidth: 0 };

const pickCurrencyFields = (row) => Object.fromEntries(CURRENCY_FIELDS.map((field) => [field, row?.[field] ?? null]));

// Field descriptor for the shared DynamicFromLookup (ACCURRENCY select box + criteria grid, like the legacy
// TDBArgosLookupEdit on frmACM0001.dsACCurrency).
const currencyCodeField = (name, readOnly) => ({
    name,
    label: "Code",
    tableName: "SPOPENITEMS",
    ignoredblocalization: true,
    isLabelChanged: true,
    required: false,
    readOnly,
    lookupDisplayMode: DynamicLookupOptions.SelectBoxDisplayMode,
    layout: ACCurrencyLayout,
});

/**
 * Legacy uOMD0012 "SP Commissions Currency", opened from the OMD0000 Commissions toolbar's Options menu
 * (pubShowCommissionsCurrencyForm). Shows the currency set of one commission line; the Code boxes are
 * only enabled for a foreign type (uOMD0012 edtTransCurrencyTypeChange / edtPayCurrencyTypeChange).
 * Presentational only — the caller saves the changed fields through the grid, as an inline edit would.
 * Mount it per open: the fields are read from `row` once.
 */
function CommissionsCurrencyPopup({ row, readOnly, onClose, onApply }) {
    const [values, setValues] = useState(() => pickCurrencyFields(row));
    const transCurrencyRef = useRef(null);
    const payCurrencyRef = useRef(null);

    const timingOptions = useMemo(
        () => (row?.RateTiming === CommissionRateTimings.OMHeaderAtCommit ? [...rateTimingOptions, orderRateTimingOption] : rateTimingOptions),
        [row]
    );

    const setField = useCallback((field, value) => {
        setValues((current) => ({ ...current, [field]: value }));
    }, []);

    // Only the fields the user actually changed go to the grid, so an untouched popup saves nothing.
    const handleOk = useCallback(() => {
        const original = pickCurrencyFields(row);
        const changed = Object.fromEntries(
            CURRENCY_FIELDS.filter((field) => values[field] !== original[field]).map((field) => [field, values[field]])
        );
        onClose();
        if (!readOnly && Object.keys(changed).length > 0) {
            onApply(changed);
        }
    }, [row, values, readOnly, onClose, onApply]);

    const isTransForeign = values.TransCurrencyType === CurrencyTypes.Foreign;
    const isPayForeign = values.PayCurrencyType === CurrencyTypes.Foreign;

    return (
        <Popup
            visible={true}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showTitle={true}
            title={CommissionMessages.CurrencyFormTitle}
            width={460}
            height="auto"
            showCloseButton={true}
        >
            <div style={{ padding: "4px 8px 8px 8px" }}>
                <OperationFieldset legend="Currency - Transaction" first>
                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <SelectBox
                                label="Type"
                                labelMode="static"
                                stylingMode="outlined"
                                items={currencyTypeOptions}
                                valueExpr="id"
                                displayExpr="text"
                                value={values.TransCurrencyType}
                                readOnly={readOnly}
                                onValueChanged={(e) => setField("TransCurrencyType", e.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            {/* editMode greys the box out and blocks it, like ab_enableControl(False) on a home type. */}
                            <DynamicFromLookup
                                fieldRef={transCurrencyRef}
                                name="RTransCurrencyCode"
                                required={false}
                                field={currencyCodeField("RTransCurrencyCode", readOnly || !isTransForeign)}
                                formFields={{ RTransCurrencyCode: values.RTransCurrencyCode ?? "" }}
                                layout={ACCurrencyLayout}
                                editMode={!isTransForeign}
                                onValueChanged={(e) => setField("RTransCurrencyCode", e.value ?? null)}
                            />
                        </div>
                    </div>
                </OperationFieldset>
                <OperationFieldset legend="Currency - Payments">
                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <SelectBox
                                label="Type"
                                labelMode="static"
                                stylingMode="outlined"
                                items={currencyTypeOptions}
                                valueExpr="id"
                                displayExpr="text"
                                value={values.PayCurrencyType}
                                readOnly={readOnly}
                                onValueChanged={(e) => setField("PayCurrencyType", e.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <DynamicFromLookup
                                fieldRef={payCurrencyRef}
                                name="RPayCurrencyCode"
                                required={false}
                                field={currencyCodeField("RPayCurrencyCode", readOnly || !isPayForeign)}
                                formFields={{ RPayCurrencyCode: values.RPayCurrencyCode ?? "" }}
                                layout={ACCurrencyLayout}
                                editMode={!isPayForeign}
                                onValueChanged={(e) => setField("RPayCurrencyCode", e.value ?? null)}
                            />
                        </div>
                    </div>
                </OperationFieldset>
                <OperationFieldset legend="Rate">
                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <SelectBox
                                label="Timing"
                                labelMode="static"
                                stylingMode="outlined"
                                items={timingOptions}
                                valueExpr="id"
                                displayExpr="text"
                                value={values.RateTiming}
                                readOnly={readOnly}
                                onValueChanged={(e) => setField("RateTiming", e.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <NumberBox
                                label="Rate"
                                labelMode="static"
                                stylingMode="outlined"
                                format={CURRENCY_RATE_FORMAT}
                                value={values.CurrencyRate}
                                readOnly={readOnly}
                                onValueChanged={(e) => setField("CurrencyRate", e.value)}
                            />
                        </div>
                    </div>
                </OperationFieldset>
                <OperationDialogFooter onOk={handleOk} onCancel={onClose} />
            </div>
        </Popup>
    );
}

export default CommissionsCurrencyPopup;
